/* ==========================================================================
   OSERE — Contrato de estado de compra
   Ver design/wireframe-home.md, regla de gobierno 4 y sección 3.

   Dos custom elements, sin librerías:

   1. <os-buy-state>   La ÚNICA fuente de verdad del variant id de la página.
                       El hero (sección 2), la hoja de la barra fija (sección 3),
                       el pivote (sección 9) y el módulo de cierre (sección 15)
                       leen y escriben aquí. Nunca guardan estado propio.

   2. <os-barra-compra> El comportamiento de la barra fija: cuándo entra, cuándo
                       se retira y la hoja inferior. Vive en este archivo para no
                       añadir una segunda petición en la ruta crítica de compra.

   CONTRATO DE DOM — lo que pueden usar las otras secciones
   --------------------------------------------------------------------------
   [data-os-variant="<id>"]           Control. Al hacer click (o change, si es un
                                      radio nativo) pasa a ser la variante activa.
   [data-os-variant-display="<campo>"] Nodo sincronizado. Campos: id · titulo ·
                                      precio · cta. En un <input> escribe .value,
                                      en cualquier otro nodo escribe .textContent.
   [data-os-variant-submit]           Se deshabilita cuando la variante no está
                                      disponible.
   .os-is-activo                      Clase que reciben los controles de la
                                      variante vigente.
   html.os-variante-agotada           Clase global cuando la variante activa no
                                      está disponible. Sirve para que cualquier
                                      sección diseñe su estado AGOTADO sin JS.

   EVENTO
   --------------------------------------------------------------------------
   document.addEventListener('os:variant:change', (ev) => ev.detail.variantId)
   detail: { variantId, variante, disponible, anterior, origen }
   Se emite también al montar, con origen 'init', para que las secciones que
   monten después puedan sincronizarse sin esperar a una interacción.

   API
   --------------------------------------------------------------------------
   window.OsBuyState.variantId          lectura
   window.OsBuyState.variantId = id     escritura
   window.OsBuyState.set(id, 'origen')  escritura con origen declarado
   window.OsBuyState.buscar(id)         devuelve el objeto de la variante

   DEGRADACIÓN
   --------------------------------------------------------------------------
   Si este archivo no carga: <os-buy-state> queda como un elemento desconocido
   (display: contents), la barra fija nunca aparece y el <form> nativo de Dawn
   sigue enviando la variante por defecto a /cart/add. No se rompe nada.
   ========================================================================== */

(function () {
  'use strict';

  var EVENTO = 'os:variant:change';

  /* ------------------------------------------------------------------------
     1. <os-buy-state> — la fuente de verdad
     ------------------------------------------------------------------------ */

  if (!customElements.get('os-buy-state')) {
    customElements.define(
      'os-buy-state',
      class OsBuyState extends HTMLElement {
        static get observedAttributes() {
          return ['data-variant-id'];
        }

        connectedCallback() {
          if (this.listo) return;
          this.listo = true;

          var datos = this.querySelector('[data-os-variants]');
          try {
            this.variantes = datos ? JSON.parse(datos.textContent) : [];
          } catch (error) {
            this.variantes = [];
          }

          this.onControl = function (ev) {
            var destino = ev.target;
            if (!destino || !destino.closest) return;
            var control = destino.closest('[data-os-variant]');
            if (!control || control.disabled) return;
            // Un <a> con href degrada a ?variant=… sin JS; con JS no navega.
            if (control.tagName === 'A' && control.getAttribute('href')) ev.preventDefault();
            this.set(control.getAttribute('data-os-variant'), 'control');
          }.bind(this);

          document.addEventListener('click', this.onControl);
          document.addEventListener('change', this.onControl);

          window.OsBuyState = this;
          this.sincronizar('init', null);
        }

        disconnectedCallback() {
          document.removeEventListener('click', this.onControl);
          document.removeEventListener('change', this.onControl);
        }

        attributeChangedCallback(nombre, anterior, actual) {
          if (!this.listo || anterior === actual) return;
          var origen = this.origen || 'atributo';
          this.origen = null;
          this.sincronizar(origen, anterior);
        }

        get variantId() {
          return this.getAttribute('data-variant-id') || '';
        }

        set variantId(valor) {
          this.set(valor, 'api');
        }

        get variante() {
          return this.buscar(this.variantId);
        }

        buscar(id) {
          var clave = String(id);
          for (var i = 0; i < this.variantes.length; i++) {
            if (String(this.variantes[i].id) === clave) return this.variantes[i];
          }
          return null;
        }

        set(id, origen) {
          var clave = id == null ? '' : String(id);
          if (!clave || clave === this.variantId) return false;
          if (this.variantes.length && !this.buscar(clave)) return false;
          this.origen = origen || 'api';
          this.setAttribute('data-variant-id', clave);
          return true;
        }

        sincronizar(origen, anterior) {
          var id = this.variantId;
          var variante = this.buscar(id);
          // Si el JSON de variantes no se pudo leer, NO se toca la
          // disponibilidad: manda lo que pintó Liquid. Reactivar por error un
          // botón agotado sería peor que no hacer nada.
          var disponible = variante ? variante.disponible !== false : null;
          var self = this;

          document.querySelectorAll('[data-os-variant]').forEach(function (el) {
            var activo = String(el.getAttribute('data-os-variant')) === String(id);
            el.classList.toggle('os-is-activo', activo);
            if (el.matches('input[type="radio"], input[type="checkbox"]')) el.checked = activo;
            if (el.getAttribute('role') === 'radio') el.setAttribute('aria-checked', String(activo));
            else if (el.tagName === 'BUTTON') el.setAttribute('aria-pressed', String(activo));
          });

          document.querySelectorAll('[data-os-variant-display]').forEach(function (el) {
            var campo = el.getAttribute('data-os-variant-display');
            var texto = null;
            if (campo === 'id') texto = id;
            else if (campo === 'cta') {
              if (disponible === null) return;
              texto = disponible ? self.getAttribute('data-cta-add') : self.getAttribute('data-cta-agotado');
            } else if (variante) texto = variante[campo];
            if (texto == null) return;
            if (el.tagName === 'INPUT' || el.tagName === 'SELECT' || el.tagName === 'TEXTAREA') el.value = texto;
            else el.textContent = texto;
          });

          if (disponible !== null) {
            document.querySelectorAll('[data-os-variant-submit]').forEach(function (el) {
              el.disabled = !disponible;
              el.setAttribute('aria-disabled', String(!disponible));
            });
            document.documentElement.classList.toggle('os-variante-agotada', !disponible);
          }

          document.dispatchEvent(
            new CustomEvent(EVENTO, {
              bubbles: true,
              detail: {
                variantId: id,
                variante: variante,
                disponible: disponible,
                anterior: anterior || null,
                origen: origen || 'init',
              },
            })
          );
        }
      }
    );
  }

  /* ------------------------------------------------------------------------
     2. <os-barra-compra> — cuándo se ve la barra, y la hoja inferior

     Nace cuando el CTA del hero sale del viewport por arriba y se retira
     mientras el módulo de cierre (#comprar) está en pantalla, para no tener
     nunca dos botones activos. Vuelve cuando #comprar se va por arriba: el
     tramo del pie es la parte más larga de la página y no puede quedar sin
     forma de comprar.

     Un IntersectionObserver sobre una centinela de 1px que el propio JS
     inserta detrás del CTA del hero. Ningún listener de scroll.
     ------------------------------------------------------------------------ */

  if (!customElements.get('os-barra-compra')) {
    customElements.define(
      'os-barra-compra',
      class OsBarraCompra extends HTMLElement {
        connectedCallback() {
          this.barra = this.querySelector('[data-os-barra]');
          if (!this.barra || this.montada) return;
          this.montada = true;

          this.pasadoElHero = false;
          this.enElCierre = false;
          this.visible = null;

          // Reserva el hueco del <body> una sola vez: la barra no puede tapar
          // el último elemento de la página. Se hace al montar y no se vuelve
          // a tocar, para no provocar CLS en cada aparición.
          document.documentElement.classList.add('os-barra-montada');

          this.observar();
          this.prepararHoja();
        }

        observar() {
          if (!('IntersectionObserver' in window)) {
            this.pasadoElHero = true;
            this.pintar();
            return;
          }

          var self = this;
          var selAncla = this.getAttribute('data-mostrar-tras');
          var ancla = selAncla ? document.querySelector(selAncla) : null;

          if (ancla) {
            var centinela = document.createElement('div');
            centinela.className = 'os-barra__centinela';
            centinela.setAttribute('aria-hidden', 'true');
            ancla.insertAdjacentElement('afterend', centinela);
            /* `data-ancla-modo="arriba"` (por defecto) es el comportamiento de la
               home: la barra nace cuando el hero se ha ido POR ARRIBA, y antes
               de eso no aparece porque el hero ya trae su propio botón.

               En la PDP eso no vale y el fallo es silencioso: el CTA del buybox
               cae en y≈900, o sea bajo el pliegue, así que al cargar el ancla
               está por DEBAJO del viewport —`top > 0`— y la condición no se
               cumple nunca. Resultado: móvil sin ningún control de compra en
               pantalla hasta que el usuario baja. Con "ambos", la barra está
               siempre que el ancla no se vea, venga de donde venga. */
            var modo = self.getAttribute('data-ancla-modo');
            new IntersectionObserver(function (entradas) {
              entradas.forEach(function (entrada) {
                self.pasadoElHero = !entrada.isIntersecting &&
                  (modo === 'ambos' || entrada.boundingClientRect.top < 0);
                self.pintar();
              });
            }).observe(centinela);
          } else {
            // Sin hero en la plantilla (PDP, páginas informativas) la barra vive
            // desde el primer píxel: es la única afordancia de compra.
            this.pasadoElHero = true;
          }

          var selCierre = this.getAttribute('data-ocultar-en');
          var cierre = selCierre ? document.querySelector(selCierre) : null;
          if (cierre) {
            new IntersectionObserver(function (entradas) {
              entradas.forEach(function (entrada) {
                self.enElCierre = entrada.isIntersecting;
                self.pintar();
              });
            }).observe(cierre);
          }

          this.pintar();
        }

        pintar() {
          var mostrar = this.pasadoElHero && !this.enElCierre;
          if (mostrar === this.visible) return;
          this.visible = mostrar;
          this.barra.classList.toggle('os-barra--visible', mostrar);
          this.barra.setAttribute('aria-hidden', String(!mostrar));
          if (mostrar) this.barra.removeAttribute('inert');
          else this.barra.setAttribute('inert', '');
          // El botón flotante de WhatsApp del pie se eleva leyendo esta clase.
          document.documentElement.classList.toggle('os-barra-visible', mostrar);
        }

        prepararHoja() {
          this.hoja = this.querySelector('[data-os-hoja]');
          this.disparador = this.querySelector('[data-os-hoja-abrir]');
          if (!this.hoja || !this.disparador || typeof this.hoja.showModal !== 'function') return;

          var self = this;

          // <dialog> aporta Escape, trampa de foco y devolución del foco al
          // disparador de forma nativa. No se reimplementa nada de eso.
          this.disparador.addEventListener('click', function (ev) {
            ev.preventDefault();
            self.hoja.showModal();
          });

          this.hoja.addEventListener('click', function (ev) {
            if (ev.target === self.hoja) self.hoja.close();
          });

          this.hoja.querySelectorAll('[data-os-hoja-cerrar]').forEach(function (boton) {
            boton.addEventListener('click', function () {
              self.hoja.close();
            });
          });

          document.addEventListener(EVENTO, function (ev) {
            if (self.hoja.open && ev.detail && ev.detail.origen === 'control') self.hoja.close();
          });
        }
      }
    );
  }
})();
